/**
 * Преобразует причины спайка из API в понятные пользователю фразы.
 * API может отдавать reasonsJson как массив объектов { code, value? } (новый формат)
 * или массив строк (legacy).
 */

export interface SpikeReasonCode {
  code: string;
  value?: string;
}

/** Коды причин → текст для пользователя (value подставляется где указано) */
const CODE_LABELS: Record<string, string | ((value?: string) => string)> = {
  mentions_z: 'Mentions well above recent average',
  mentions_ratio: (value) => (value ? `${value}× more mentions` : 'More mentions than baseline'),
  momentum_mentions: (value) => (value ? `Momentum +${value} mentions` : 'Momentum in mentions'),
  users_z: 'Many more unique users than usual',
  users_ratio: (value) => (value ? `${value}× more unique users` : 'More unique users than baseline'),
  broad_participation: (value) => (value ? `Broad participation (${value} users)` : 'Broad participation'),
  two_of_three: 'Confirmed: 2 of last 3 windows',
  rising: 'Activity rising vs previous 30 min',
  new_breakout: 'New breakout (no prior baseline)',
  anti_spam_single_user: '', // скрыть
  anti_spam_few_users: '',   // скрыть
};

/**
 * Форматирует одну причину по коду и опциональному value.
 * Возвращает пустую строку для кодов, которые нужно скрыть (anti_spam_*).
 */
export function formatSpikeReasonFromCode(code: string, value?: string): string {
  const label = CODE_LABELS[code];
  if (label === undefined) return code.replace(/_/g, ' ');
  if (typeof label === 'function') return label(value ?? '');
  return label;
}

/**
 * Парсит reasonsJson: массив объектов { code, value? } или массив строк (legacy).
 */
export function parseReasonsJson(reasonsJson: string | undefined): SpikeReasonCode[] | string[] {
  if (!reasonsJson || typeof reasonsJson !== 'string') return [];
  try {
    const parsed = JSON.parse(reasonsJson);
    if (!Array.isArray(parsed)) return [];
    if (parsed.length === 0) return [];
    const first = parsed[0];
    if (typeof first === 'object' && first !== null && 'code' in first) {
      return parsed.filter((r): r is SpikeReasonCode => typeof r === 'object' && r !== null && typeof (r as SpikeReasonCode).code === 'string');
    }
    return parsed.filter((r): r is string => typeof r === 'string');
  } catch {
    return [];
  }
}

/**
 * Преобразует массив причин (коды или строки) в массив готовых фраз для отображения.
 */
export function reasonsToDisplayStrings(reasons: SpikeReasonCode[] | string[]): string[] {
  if (reasons.length === 0) return [];
  const first = reasons[0];
  if (typeof first === 'string') {
    return (reasons as string[]).map((r) => formatSpikeReasonLegacy(r));
  }
  return (reasons as SpikeReasonCode[])
    .map((r) => formatSpikeReasonFromCode(r.code, r.value))
    .filter(Boolean);
}

/**
 * Форматирует массив причин в одну строку (для отображения в UI).
 * Принимает уже распарсенный массив кодов или массив строк.
 */
export function formatSpikeReasons(reasons: string[] | SpikeReasonCode[] | undefined, separator = ' · '): string {
  if (!reasons || reasons.length === 0) return '';
  const first = reasons[0];
  const strings = typeof first === 'string'
    ? (reasons as string[]).map((r) => formatSpikeReasonLegacy(r))
    : reasonsToDisplayStrings(reasons as SpikeReasonCode[]);
  return strings.filter(Boolean).join(separator);
}

// ----- Legacy: строковые причины (regex) -----

const REASON_MAP: Array<{ pattern: RegExp | string; label: string | ((...groups: string[]) => string) }> = [
  { pattern: /mentions z=([\d.]+) vs \d+h baseline/i, label: 'Mentions well above recent average' },
  { pattern: /mentions z≥[\d.]+ vs .*baseline/i, label: 'Mentions spike vs baseline' },
  { pattern: /mentions.*baseline/i, label: 'Mentions above recent average' },
  { pattern: /unique users z≥(\d+) vs baseline/i, label: 'Many more unique users than usual' },
  { pattern: /unique users (?:up|↑) ([\d.]+)×/i, label: (mult: string) => `${mult}× more unique users` },
  { pattern: /unique users (?:up|↑)/i, label: 'More unique users than baseline' },
  { pattern: /unique users.*baseline/i, label: 'Unique users above average' },
  { pattern: /broad participation \((\d+) users?\)/i, label: (n: string) => `Broad participation (${n} users)` },
  { pattern: /broad participation/i, label: 'Broad participation' },
  { pattern: /rising vs previous window/i, label: 'Activity rising vs previous 30 min' },
  { pattern: /confirmed: (\d+) of last (\d+) windows?/i, label: (a: string, b: string) => `Sustained interest (${a} of last ${b} windows)` },
  { pattern: /confirmed:.*windows?/i, label: 'Sustained interest over recent windows' },
];

function matchLabel(entry: (typeof REASON_MAP)[0], text: string): string | null {
  if (typeof entry.pattern === 'string') {
    if (!text.toLowerCase().includes(entry.pattern.toLowerCase())) return null;
    return typeof entry.label === 'function' ? (entry.label as () => string)() : entry.label;
  }
  const m = text.match(entry.pattern);
  if (!m) return null;
  return typeof entry.label === 'function' ? (entry.label as (...a: string[]) => string)(...m.slice(1)) : entry.label;
}

/** Форматирует одну причину в виде строки (legacy — когда API отдаёт массив строк). */
export function formatSpikeReasonLegacy(reason: string): string {
  const t = reason.trim();
  for (const entry of REASON_MAP) {
    const label = matchLabel(entry, t);
    if (label) return label;
  }
  if (t.includes('vs baseline')) return t.replace(/\s*vs \d*h baseline\s*/i, ' ').replace(/\bz=\d+(\.\d+)?\b/gi, '').trim() || t;
  if (t.includes('z≥')) return t.replace(/\bz≥\d+\b/gi, '').replace(/\s*vs baseline\s*/gi, ' ').trim() || t;
  return t;
}
